import { getUserSettings, type Account } from '@/api/auth';
import type { AvailableSelectionsView } from '@/api/custom-bets';
import {
  betslipToggleCustom,
} from '@/betslip/betslip-store';
import {
  betslipToggleOptionsFromAccount,
  buildBetslipDraft,
} from '@/betslip/build-draft';
import {
  isCustomSelectionInTicket,
  type CustomBetToggleReason,
} from '@/domain/custom-bets';
import type { BetslipTicket } from '@/domain/betslip';
import type { EventOutcome } from '@/ui/sports/EventRow';
import type { BetslipEventSource } from './select-outcome';

function normalizeId(value: string | number | null | undefined): string {
  return String(value ?? '').trim().toLowerCase();
}

function normalizeSpecifier(raw: string | null | undefined): string {
  return (raw ?? '').trim();
}

/** Specifier values after stripping `key=` (`total=2.5` → `2.5`). */
function specifierValues(raw: string): string[] {
  const s = normalizeSpecifier(raw);
  if (!s) return [];
  return s
    .split('|')
    .map((part) => {
      const eq = part.indexOf('=');
      return (eq >= 0 ? part.slice(eq + 1) : part).trim();
    })
    .filter(Boolean)
    .sort();
}

/**
 * Exact match, or value-set equality after stripping keys
 * (`total=2.5` ≡ `2.5`; empty ≡ empty).
 */
export function specifierEquals(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  const left = normalizeSpecifier(a);
  const right = normalizeSpecifier(b);
  if (left === right) return true;
  const lv = specifierValues(left);
  const rv = specifierValues(right);
  if (lv.length === 0 && rv.length === 0) return true;
  if (lv.length !== rv.length) return false;
  return lv.every((value, index) => value === rv[index]);
}

function viewEventId(view: AvailableSelectionsView): string {
  const record = view as AvailableSelectionsView & { eventId?: number };
  return normalizeId(view.EventId ?? record.eventId);
}

function viewMarkets(view: AvailableSelectionsView) {
  const record = view as AvailableSelectionsView & {
    markets?: AvailableSelectionsView['Markets'];
  };
  return view.Markets ?? record.markets ?? [];
}

/**
 * Legacy `IsBetBuilderOutcomeAvailable` — when AvailableSelections is set,
 * only outcomes listed as Active && !Conflict stay selectable. Markets omitted
 * from the list, market Conflict, outcome Conflict, Active=false, or omitted
 * outcomes are blocked. Already-selected legs stay available via the caller.
 */
export function isBetBuilderOutcomeAvailable(
  available: AvailableSelectionsView[] | null | undefined,
  marketId: string | undefined,
  outcomeId: string | undefined,
  specifier: string | null | undefined,
  outcomeCode?: string,
  eventId?: string | number,
): boolean {
  if (!available?.length) return true;

  const marketKey = normalizeId(marketId);
  if (!marketKey) return false;

  const keys = new Set<string>();
  const primary = normalizeId(outcomeId);
  const secondary = normalizeId(outcomeCode);
  if (primary) keys.add(primary);
  if (secondary) keys.add(secondary);
  if (keys.size === 0) return false;

  const eventKey = eventId !== undefined && eventId !== null ? normalizeId(eventId) : '';
  let sawMatchingEvent = false;

  for (const eventView of available) {
    if (eventKey && viewEventId(eventView) !== eventKey) continue;
    sawMatchingEvent = true;

    const markets = viewMarkets(eventView);
    for (const market of markets) {
      const record = market as typeof market & {
        marketId?: string;
        specifier?: string | null;
        conflict?: boolean | null;
        outcomes?: typeof market.Outcomes;
      };
      const apiMarketId = normalizeId(market.MarketId ?? record.marketId);
      if (apiMarketId !== marketKey) continue;
      if (!specifierEquals(market.Specifier ?? record.specifier, specifier)) continue;

      if (market.Conflict === true || record.conflict === true) return false;

      const outcomes = market.Outcomes ?? record.outcomes ?? [];
      if (!outcomes.length) return false;

      for (const outcome of outcomes) {
        const outRecord = outcome as typeof outcome & {
          outcomeId?: string;
          active?: boolean;
          conflict?: boolean | null;
        };
        const apiOutcomeId = normalizeId(outcome.OutcomeId ?? outRecord.outcomeId);
        if (!keys.has(apiOutcomeId)) continue;
        if (outcome.Conflict === true || outRecord.conflict === true) return false;
        if (outcome.Active === false || outRecord.active === false) return false;
        return true;
      }
      // Market+specifier matched but this outcome is not in the list.
      return false;
    }
  }

  // Event present but market/line omitted from AvailableSelections → blocked.
  // If eventId was supplied and no matching event view exists, also block.
  if (eventKey && !sawMatchingEvent) return false;
  return false;
}

/** Mark BB outcomes selected / locked from slip + AvailableSelections. */
export function withCustomBetOutcomeState(
  ticket: BetslipTicket,
  eventId: string | number,
  outcomes: EventOutcome[],
  available: AvailableSelectionsView[] | null,
): EventOutcome[] {
  return outcomes.map((outcome) => {
    if (outcome.state === 'locked' || outcome.state === 'suspended') return outcome;
    const marketId = outcome.marketId ?? outcome.id.split(':')[0];
    const outcomeCode = outcome.outcomeCode ?? outcome.outcome;
    const selected = isCustomSelectionInTicket(ticket, eventId, {
      MarketId: marketId ?? null,
      Outcome: outcomeCode,
      OutcomeId: outcomeCode,
      Specifier: outcome.specifier ?? null,
    });
    if (selected) return { ...outcome, state: 'selected' as const };

    const availableOk = isBetBuilderOutcomeAvailable(
      available,
      marketId,
      outcomeCode,
      outcome.specifier,
      outcomeCode,
      eventId,
    );
    if (!availableOk) return { ...outcome, state: 'locked' as const };
    return outcome;
  });
}

export type SelectCustomOutcomeResult =
  | { ok: true }
  | { ok: false; reason: CustomBetToggleReason };

/** Toggle a Bet Builder selection for an event/outcome pair. */
export function selectCustomOutcomeForEvent(
  event: BetslipEventSource,
  outcome: EventOutcome,
  account?: Account | null,
): SelectCustomOutcomeResult {
  const draft = buildBetslipDraft(event, outcome, {
    feedType: typeof account?.FeedType === 'number' ? account.FeedType : undefined,
  });
  if (!draft) return { ok: false, reason: 'invalid' };

  const result = betslipToggleCustom(
    {
      ...draft,
      marketName: outcome.marketName ?? draft.market,
    },
    betslipToggleOptionsFromAccount(account, getUserSettings()),
  );
  if (!result.ok) return { ok: false, reason: result.reason };
  return { ok: true };
}
